import { Component, computed, effect, inject, input } from '@angular/core';
import { CompilingWidgetService } from '../../../services/compiling/widget.service';
import { CheckBoxCompilingComponent } from '../CheckBox/checkbox-compiling';
import { ComboBoxCompilingComponent } from '../ComboBox/combobox-compiling';
import { DatePickerCompilingComponent } from '../DatePicker/datepicker-compiling';
import { FileDialogCompilingComponent } from '../FileDialog/filedialog-compiling';
import { NumericUpDownCompilingComponent } from '../NumericUpDown/numericupdown-compiling';
import { RadioButtonCompilingComponent } from '../RadioButton/radiobutton-compiling';
import { MultiRadioButtonCompilingComponent } from '../MultiRadioButton/multi-radiobutton-compiling';
import { MultiplierRadioButtonCompilingComponent } from '../MultiplierRadioButton/multiplier-radiobutton-compiling';
import { StaticTextCompilingComponent } from '../StaticText/statictext-compiling';
import { TextBoxCompilingComponent } from '../TextBox/textbox-compiling';
import { AUTO_COMPILE_SUPPORTED_TYPES } from '../../../services/compiling/apply-compiled-answer';
import { AnswerPermissionsService } from '../../../services/compiling/state/answer-permissions.service';
import { AutoCompileButtonComponent } from './auto-compile-button.component';
import {
  COMPILED_ELEMENT_EVALUATION_PROVIDER,
  EvaluationScoreComponent,
} from './evaluation-score.component';

@Component({
  selector: 'lib-base-element-compiling',
  template: `
    @let widget = widgetService.widget();
    @if (widgetService.visible()) {
      @if (widget) {
        @switch (widget._mappedType) {
          @case ('TextBox') {
            <lib-textbox-compiling></lib-textbox-compiling>
          }
          @case ('NumericUpDown') {
            <lib-numericupdown-compiling></lib-numericupdown-compiling>
          }
          @case ('DatePicker') {
            <lib-datepicker-compiling></lib-datepicker-compiling>
          }
          @case ('ComboBox') {
            <lib-combobox-compiling></lib-combobox-compiling>
          }
          @case ('CheckBox') {
            <lib-checkbox-compiling></lib-checkbox-compiling>
          }
          @case ('RadioButton') {
            <lib-radiobutton-compiling></lib-radiobutton-compiling>
          }
          @case ('MultiRadioButton') {
            <lib-multi-radiobutton-compiling></lib-multi-radiobutton-compiling>
          }
          @case ('MultiplierRadioButton') {
            <lib-multiplier-radiobutton-compiling></lib-multiplier-radiobutton-compiling>
          }
          @case ('FileDialog') {
            <lib-filedialog-compiling></lib-filedialog-compiling>
          }
          @case ('StaticText') {
            <lib-statictext-compiling></lib-statictext-compiling>
          }
        }

        @if (autoCompileSupported() && permissions.canEditTechnicalOffer()) {
          <lib-auto-compile-button></lib-auto-compile-button>
        }
      }

      @if (evaluationResult(); as r) {
        <!-- result with max score are not influential, as they don't affect the score -->
        @if (r.maxScore > 0) {
          <lib-evaluation-score [result]="r"></lib-evaluation-score>
        }
      }
    }
  `,
  imports: [
    TextBoxCompilingComponent,
    NumericUpDownCompilingComponent,
    DatePickerCompilingComponent,
    ComboBoxCompilingComponent,
    CheckBoxCompilingComponent,
    RadioButtonCompilingComponent,
    MultiRadioButtonCompilingComponent,
    MultiplierRadioButtonCompilingComponent,
    FileDialogCompilingComponent,
    StaticTextCompilingComponent,
    AutoCompileButtonComponent,
    EvaluationScoreComponent,
  ],
  providers: [CompilingWidgetService],
})
export class BaseElementCompilingComponent {
  public widgetService = inject(CompilingWidgetService);
  protected readonly permissions = inject(AnswerPermissionsService);
  public evaluationProvider = inject(COMPILED_ELEMENT_EVALUATION_PROVIDER, {
    optional: true,
  });

  widgetId = input.required<string>();

  readonly autoCompileSupported = computed(() => {
    const widget = this.widgetService.widget();
    return (
      !!widget && AUTO_COMPILE_SUPPORTED_TYPES.includes(widget._mappedType)
    );
  });

  readonly evaluationResult = computed(() => {
    if (!this.evaluationProvider) {
      return null;
    }

    const widgetId = this.widgetId();
    if (!widgetId) {
      return null;
    }

    return this.evaluationProvider.evaluate(widgetId)();
  });

  constructor() {
    effect(() => (this.widgetService.uniqueId = this.widgetId()));
  }
}
